import React, { useCallback } from 'react';
import { isEqual } from 'lodash';
import { Field, FieldRenderProps, RenderableProps } from 'react-final-form';

import { LocationFilter, LocationFragment } from '@occc/graphql';
import { useGetLocationsLazyQuery } from '@occc-app/dataAccessLayer/location/queries';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
  StoreMallDirectoryOutlinedIcon,
} from '@occc-app/design-system';

interface LocationSelectContainerProps
  extends Pick<AutocompleteProps, 'onFocus' | 'onBlur' | 'chipPropsIcon'> {
  value: LocationFragment[];
  onChange: (values: LocationFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
}

type LocationSelectFieldProps = {
  name: string;
  label: string;
} & Pick<LocationSelectContainerProps, 'inputProps' | 'chipPropsIcon'>;

const makeFilter = (search?: string): LocationFilter => ({
  isDeleted: false,
  siteSystemConfiguration: search ? { identifierContains: search } : undefined,
});

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetLocationsLazyQuery>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (location: LocationFragment) => ({
  value: location.uuid,
  label: location.siteSystemConfiguration[0]?.identifier || '',
  _raw: location,
});

const LocationSelect: React.FC<LocationSelectContainerProps> = (props) => (
  <LazyAutocomplete<LocationFragment, ReturnType<typeof useGetLocationsLazyQuery>['response']>
    label='Locations'
    {...props}
    useLazyQuery={useGetLocationsLazyQuery}
    makeQueryFilter={makeFilter}
    mapQueryResponseToRawArray={mapQueryResponseToRawArray}
    mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
    chipPropsIcon={StoreMallDirectoryOutlinedIcon}
  />
);

// TODO: use LazyAutocompleteField
export const LocationMultipleSelectField = (props: LocationSelectFieldProps) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<LocationFragment[]>>['render']>
  >(
    ({ input, meta }) => (
      <LocationSelect
        {...props}
        value={input.value || []}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        onChange={input.onChange}
        errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
      />
    ),
    [props],
  );

  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
};
